(() => {
  'use strict';

  // Native pixel dimensions of assets/skeleton-transparent.png. The figure is laid out at
  // this exact size and scaled down, so the browser rasterizes the layer at full resolution
  // and zooming in never stretches an undersized texture.
  const NATIVE_W = 832;
  const NATIVE_H = 1248;
  // Fraction of viewport height the specimen occupies at rest, matching the design's 80vh.
  const FIT_RATIO = 0.8;

  const KEYS = [
    { t: 0.00, s: 1.00, f: 0.50 },
    { t: 0.10, s: 1.00, f: 0.50 },
    { t: 0.26, s: 2.15, f: 0.16 },
    { t: 0.40, s: 2.15, f: 0.16 },
    { t: 0.52, s: 1.95, f: 0.33 },
    { t: 0.64, s: 1.95, f: 0.33 },
    { t: 0.76, s: 1.95, f: 0.47 },
    { t: 0.86, s: 1.95, f: 0.47 },
    // Final frame has to seat the whole specimen in the band between the fixed header and
    // the closing caption, at every viewport height.
    //
    // The old s:1.06 / f:0.50 made the figure *larger* than its resting size and centred it
    // on the viewport, so its feet landed straight on the caption and CTA and both turned to
    // mush. Lifting it alone (f:0.70) fixed that at 900px tall but pushed the skull up behind
    // the header at 720px.
    //
    // So: pull back (s below 1) until the figure fits the shortest band, and lift by just
    // enough to centre it in that band rather than in the viewport. Both terms scale with
    // viewport height, so the framing holds — roughly 55px clear above and below at 900px,
    // 30px at 720px. Pulling back also suits the "COMPLETE" caption: the camera steps away
    // to show the whole specimen.
    { t: 1.00, s: 0.78, f: 0.59 },
  ];

  const lerp = (a, b, u) => a + (b - a) * u;
  const smooth = (u) => u * u * u * (u * (u * 6 - 15) + 10);
  const noise = (a, b, ph) => 0.62 * Math.sin(a) + 0.38 * Math.sin(b + ph);

  function sample(t) {
    let i = 0;
    while (i < KEYS.length - 2 && t > KEYS[i + 1].t) i++;
    const a = KEYS[i], b = KEYS[i + 1];
    const u = smooth(Math.max(0, Math.min(1, (t - a.t) / (b.t - a.t))));
    return { s: lerp(a.s, b.s, u), f: lerp(a.f, b.f, u) };
  }

  function initHero(track) {
    const img = track.querySelector('[data-meningea-img]');
    const tilt = track.querySelector('[data-meningea-tilt]');
    const rail = track.querySelector('[data-meningea-rail]');
    const panelEls = {
      p0: track.querySelector('[data-meningea-panel="p0"]'),
      p1: track.querySelector('[data-meningea-panel="p1"]'),
      p2: track.querySelector('[data-meningea-panel="p2"]'),
      p3: track.querySelector('[data-meningea-panel="p3"]'),
      p4: track.querySelector('[data-meningea-panel="p4"]'),
    };
    if (!img) return;

    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const cameraZoom = track.dataset.cameraZoom !== 'false';
    const idleMotion = track.dataset.idleMotion !== 'false';
    const idleStrength = parseFloat(track.dataset.idleStrength) || 1;
    const pointerDepthEnabled = track.dataset.pointerDepth !== 'false';

    // Seed the damped scale at the resting fit so the first frame doesn't lerp in from 1:1.
    const cur = {
      s: (FIT_RATIO * window.innerHeight) / NATIVE_H,
      ty: 0, dx: 0, dy: 0, rz: 0, px: 0, py: 0,
    };
    let aimX = 0, aimY = 0;

    // Cached rather than read per frame: offsetHeight forces a layout flush, and doing that
    // inside a rAF loop that also writes transforms is the classic way to make a scroll
    // animation stutter. The value only changes when the viewport does.
    let cachedBoxHeight = 0;
    const boxHeight = () => cachedBoxHeight || (cachedBoxHeight = img.offsetHeight || NATIVE_H);
    window.addEventListener('resize', () => { cachedBoxHeight = 0; }, { passive: true });

    const isTouch = 'ontouchstart' in window;
    let onPointerMove, onPointerLeave;
    if (!reduced && !isTouch) {
      onPointerMove = (e) => {
        aimX = (e.clientX / window.innerWidth) * 2 - 1;
        aimY = (e.clientY / window.innerHeight) * 2 - 1;
      };
      onPointerLeave = () => { aimX = 0; aimY = 0; };
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('pointerleave', onPointerLeave, { passive: true });
    }

    const panels = () => [
      [panelEls.p0, 0.04, 0.10, -1],
      [panelEls.p1, 0.33, 0.11, 1],
      [panelEls.p2, 0.58, 0.11, -1],
      [panelEls.p3, 0.81, 0.10, 1],
      [panelEls.p4, 1.00, 0.09, 0],
    ];

    let raf = 0;
    const tick = () => {
      const r = track.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      const t = total > 0 ? Math.max(0, Math.min(1, -r.top / total)) : 0;
      const cam = cameraZoom ? sample(t) : { s: 1.0, f: 0.5 };

      // fit maps the native-size figure down to the intended on-screen height; the camera's
      // zoom multiplies on top of it. Capped so the texture is never stretched much beyond
      // native resolution for this display's pixel density.
      const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
      const fit = (FIT_RATIO * window.innerHeight) / NATIVE_H;
      const maxZoom = (1.35 * dpr) / fit;
      const zoom = Math.min(cam.s, maxZoom);

      const scaleT = fit * zoom;
      // Lifts the figure so that the point at fraction cam.f of its height lands on the
      // viewport's centre line. It has to be measured against the box the figure is
      // actually laid out in, not NATIVE_H.
      //
      // On desktop the two are the same number — the box is exactly 1248px — so this is a
      // no-op there. On phones the stylesheet gives the box a shorter height, and the
      // hardcoded 1248 was over-translating by that ratio: at the cranium and thorax
      // keyframes the specimen was pushed most of the way off the bottom of the screen, so
      // the middle of the sequence zoomed into empty paper. That is a good part of why the
      // mobile hero read as a long scroll that never arrived anywhere.
      const tyT = (0.5 - cam.f) * boxHeight() * scaleT;
      cur.s = lerp(cur.s, scaleT, 0.12);
      cur.ty = lerp(cur.ty, tyT, 0.12);
      img.style.transform = `translate(-50%, calc(-50% + ${cur.ty.toFixed(1)}px)) scale(${cur.s.toFixed(4)})`;

      if (tilt) {
        const IK = (reduced || !idleMotion) ? 0 : idleStrength;
        const tm = performance.now() / 1000;
        const w = Math.PI * 2;
        const breath = Math.sin(w * 0.09 * tm);
        const nx = noise(w * 0.037 * tm, w * 0.061 * tm, 1.7);
        const ny = noise(w * 0.043 * tm + 2.1, w * 0.055 * tm, 0.6);
        const nr = noise(w * 0.029 * tm + 0.8, w * 0.047 * tm, 2.4);
        const dxT = nx * 3.0 * IK;
        const dyT = (ny * 2.4 - breath * 1.6) * IK;
        const rzT = nr * 0.7 * IK;
        const pd = pointerDepthEnabled && !reduced ? 1 : 0;
        const pxT = aimX * 12 * pd;
        const pyT = aimY * 9 * pd;
        const prT = aimX * 1.8 * pd;
        cur.dx = lerp(cur.dx, dxT, 0.06);
        cur.dy = lerp(cur.dy, dyT, 0.06);
        cur.rz = lerp(cur.rz, rzT + prT, 0.06);
        cur.px = lerp(cur.px, pxT, 0.08);
        cur.py = lerp(cur.py, pyT, 0.08);
        const sc = 1 + 0.006 * breath * IK;
        tilt.style.transform =
          `translate(${(cur.dx + cur.px).toFixed(2)}px, ${(cur.dy + cur.py).toFixed(2)}px) ` +
          `rotate(${cur.rz.toFixed(3)}deg) scale(${sc.toFixed(4)})`;
      }

      for (const [el, c, wdt, dir] of panels()) {
        if (!el) continue;
        const o = Math.max(0, Math.min(1, (1 - Math.abs(t - c) / wdt) * 1.6));
        el.style.opacity = o.toFixed(2);
        el.style.transform = reduced ? 'none' : `translate(${(dir * (1 - o) * 34).toFixed(1)}px, 0)`;
        // All four panels occupy the same sticky viewport, and p1 (Nervous) and p3
        // (Skeletal) are both pinned right — so their buttons land on the same spot. The
        // CSS gives every panel's links pointer-events:auto, which meant the invisible
        // p3 sat on top of the visible p1 and swallowed the click: pressing "Shop
        // Nervous" opened the Skeletal collection. Opacity alone does not stop a click;
        // this does.
        el.style.pointerEvents = o > 0.5 ? 'auto' : 'none';
      }

      if (rail) rail.style.height = (t * 100).toFixed(1) + '%';

      raf = requestAnimationFrame(tick);
    };

    // The loop drives an idle float, so it never settles and never stops on its own. Left
    // ungated it kept compositing a full-screen layer at 60fps while the visitor was three
    // sections further down reading the archive — free battery drain on a phone, and it
    // competes with the scrolling it is no longer animating. The observer keeps it running
    // only while any part of the track is on screen.
    const start = () => { if (!raf) raf = requestAnimationFrame(tick); };
    const stop = () => { if (raf) { cancelAnimationFrame(raf); raf = 0; } };

    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => { entries[0].isIntersecting ? start() : stop(); },
        { rootMargin: '10% 0px' }
      );
      observer.observe(track);
    } else {
      start();
    }

    track.addEventListener('shopify:section:unload', () => {
      stop();
      if (observer) observer.disconnect();
      if (onPointerMove) window.removeEventListener('pointermove', onPointerMove);
      if (onPointerLeave) window.removeEventListener('pointerleave', onPointerLeave);
    });
  }

  // Below the phone breakpoint the sticky hero is display:none and a static composition is
  // rendered in its place (see sections/meningea-hero.liquid). Bailing out here means no
  // rAF loop, no scroll listener and no full-screen transform layer exist at all on a
  // phone — the page scrolls with nothing competing for the frame budget.
  //
  // Matches the CSS breakpoint exactly, and is re-evaluated on rotation so a phone turned
  // landscape into a tablet-width viewport still gets the camera.
  const PHONE = '(max-width: 749px)';
  let started = false;

  function init() {
    if (started || window.matchMedia(PHONE).matches) return;
    started = true;
    document.querySelectorAll('[data-meningea-track]').forEach(initHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.matchMedia(PHONE).addEventListener('change', init);
})();
